import { envConfig } from '#constants/envConfig';

/**
 * `localStorage`, который не роняет страницу.
 *
 * <p>Safari с «Блокировать все cookie» и Chrome с запрещёнными данными сайта бросают
 * `SecurityError` на самом обращении к `window.localStorage` — не на записи, а на чтении
 * свойства. Пока обёртка ходила туда напрямую, это падение было фатальным: `gqlFetcher`
 * читает здесь `reCaptchaToken` перед каждым запросом, поэтому у такого человека
 * разваливался любой экран, который хоть что-то грузит (GlitchTip 2431/2432, 20.08:
 * лендинг Fortnite ушёл в error boundary, а до него — три хромовых случая в августе).
 *
 * <p>Поэтому каждое обращение к настоящему хранилищу обёрнуто, а подстраховкой служит Map
 * в памяти вкладки. Это не только про «не упасть»: капча продолжает работать — `useReCaptcha`
 * кладёт токен, `gqlFetcher` его забирает, — просто ничего не переживает перезагрузку.
 * Человеку, запретившему хранилище, ровно этого и хотелось.
 */
const memory = new Map<string, string>();

/**
 * Настоящее хранилище или `null`, если его нет: сервер, приватный режим, запрет данных сайта.
 * Обращение к свойству вынесено сюда, потому что бросает именно оно.
 */
const getStore = (): Storage | null => {
  if (!envConfig.IN_BROWSER) {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const setItem = (key: string, item: string) => {
  if (!envConfig.IN_BROWSER) {
    return;
  }

  const store = getStore();

  if (!store) {
    memory.set(key, item);
    return;
  }

  try {
    store.setItem(key, item);
    // Запись прошла — снимаем возможный дубль из памяти, чтобы чтение не подняло
    // старое значение вместо нового.
    memory.delete(key);
  } catch {
    // Хранилище есть, но записать нельзя: квота или приватный режим Safari, где `setItem`
    // падает, а `getItem` работает.
    memory.set(key, item);
  }
};

const deleteItem = (key: string) => {
  if (!envConfig.IN_BROWSER) {
    return;
  }

  memory.delete(key);

  try {
    getStore()?.removeItem(key);
  } catch {
    // Удалять нечего — хранилище закрылось после того, как мы в него писали.
  }
};

const getItem = (key: string) => {
  if (!envConfig.IN_BROWSER) {
    return null;
  }

  const store = getStore();

  if (!store) {
    return memory.get(key) ?? null;
  }

  try {
    return store.getItem(key) ?? memory.get(key) ?? null;
  } catch {
    return memory.get(key) ?? null;
  }
};

const checkItem = (key: string): boolean => !!getItem(key);

const getItems = (keys: string[]) => {
  if (!envConfig.IN_BROWSER) {
    return [];
  }

  return Object.fromEntries(keys.map((token) => [token, getItem(token)]));
};

export const localStorage = {
  setItem,
  getItem,
  deleteItem,
  checkItem,
  getItems,
};
